import React, { useEffect, useMemo, useRef, useState } from 'react';

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  minHeight,
  maxHeight,
  resizable = false,
  editorClassName = '',
  toolbarClassName = '',
}) => {
  const editorRef = useRef(null);
  const [blockStyle, setBlockStyle] = useState('p');
  const savedRangeRef = useRef(null);

  const parsePx = (v) => {
    if (!v || typeof v !== 'string') return null;
    const m = v.trim().match(/^(-?\d+(?:\.\d+)?)px$/i);
    if (!m) return null;
    const n = Number(m[1]);
    return Number.isFinite(n) ? n : null;
  };

  const resolvedMaxHeight = useMemo(() => {
    // Backward compatible default: cap editor height unless explicitly resizable.
    if (maxHeight === null) return undefined;
    if (typeof maxHeight !== 'undefined') return maxHeight;
    if (resizable) return undefined;
    return '220px';
  }, [maxHeight, resizable]);

  const resolvedMinHeight = minHeight || '140px';

  const safeMaxHeight = useMemo(() => {
    if (!resolvedMaxHeight) return resolvedMaxHeight;
    const minPx = parsePx(resolvedMinHeight);
    const maxPx = parsePx(resolvedMaxHeight);
    if (minPx !== null && maxPx !== null && maxPx < minPx) {
      return `${minPx}px`;
    }
    return resolvedMaxHeight;
  }, [resolvedMaxHeight, resolvedMinHeight]);

  const isEmpty = useMemo(() => {
    if (!value) return true;
    const text = String(value)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return text.length === 0;
  }, [value]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = value || '';
    if (el.innerHTML !== next) {
      el.innerHTML = next;
    }
  }, [value]);

  useEffect(() => {
    const handleSelectionChange = () => {
      const el = editorRef.current;
      if (!el) return;

      const active = document.activeElement;
      if (active !== el) return;

      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const containerEl = container.nodeType === Node.ELEMENT_NODE ? container : container.parentElement;
      if (!containerEl || !el.contains(containerEl)) return;

      savedRangeRef.current = range.cloneRange();
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const saveSelection = () => {
    const el = editorRef.current;
    if (!el) return;
    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0).cloneRange();

    // Only save if selection is inside the editor
    const container = range.commonAncestorContainer;
    const containerEl = container.nodeType === Node.ELEMENT_NODE ? container : container.parentElement;
    if (!containerEl || !el.contains(containerEl)) return;

    savedRangeRef.current = range;
  };

  const restoreSelection = () => {
    const el = editorRef.current;
    const range = savedRangeRef.current;
    if (!el || !range) return;
    try {
      el.focus();
      const sel = window.getSelection && window.getSelection();
      if (!sel) return;

      const container = range.commonAncestorContainer;
      const containerEl = container.nodeType === Node.ELEMENT_NODE ? container : container.parentElement;
      if (!containerEl || !el.contains(containerEl)) return;

      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      // ignore
    }
  };

  const onToolbarButtonMouseDown = (e) => {
    // Prevent focus from leaving the editor which collapses selection
    e.preventDefault();
    restoreSelection();
  };

  const onToolbarSelectMouseDown = () => {
    // Do NOT preventDefault here, otherwise the dropdown won't open.
    // Just save the current selection so applying the style works.
    saveSelection();
  };

  const exec = (command, commandValue = null) => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();
    el.focus();

    const applied = document.execCommand(command, false, commandValue);

    // Browser compatibility fallback for formatBlock
    if (!applied && command === 'formatBlock' && typeof commandValue === 'string') {
      const stripped = commandValue.replace(/[<>]/g, '');
      if (stripped) {
        document.execCommand(command, false, stripped);
        document.execCommand(command, false, stripped.toUpperCase());
      }
    }

    saveSelection();
    onChange(el.innerHTML);
  };

  const applyBlock = (tag) => {
    setBlockStyle(tag);
    exec('formatBlock', `<${tag}>`);
  };

  const wrapSelectionWithBlock = (tagName) => {
    const el = editorRef.current;
    if (!el) return;

    restoreSelection();
    el.focus();

    const sel = window.getSelection && window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const containerEl = container.nodeType === Node.ELEMENT_NODE ? container : container.parentElement;
    if (!containerEl || !el.contains(containerEl)) return;

    if (range.collapsed) {
      const plain = (el.textContent || '').replace(/\u00A0/g, ' ').trim();
      if (!plain) return;
      exec('formatBlock', `<${tagName}>`);
      return;
    }

    const wrapper = document.createElement(tagName);
    const fragment = range.extractContents();
    wrapper.appendChild(fragment);
    range.insertNode(wrapper);

    const after = document.createRange();
    after.setStartAfter(wrapper);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    savedRangeRef.current = after.cloneRange();
    onChange(el.innerHTML);
  };

  const insertLink = () => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();
    saveSelection();
    const url = window.prompt('Enter link URL');
    if (!url) return;
    restoreSelection();
    document.execCommand('createLink', false, url);
    saveSelection();
    onChange(el.innerHTML);
  };

  const insertImage = () => {
    const el = editorRef.current;
    if (!el) return;
    restoreSelection();
    saveSelection();
    const url = window.prompt('Enter image URL');
    if (!url) return;
    restoreSelection();
    document.execCommand('insertImage', false, url);
    saveSelection();
    onChange(el.innerHTML);
  };

  const clearFormatting = () => {
    exec('removeFormat');
    exec('unlink');
    exec('formatBlock', '<p>');
  };

  return (
    <div className="w-full">
      <div className={`flex flex-wrap items-center gap-1.5 border border-gray-200 rounded-t-md bg-slate-50 px-2 py-1 ${toolbarClassName}`}>
        <select
          value={blockStyle}
          onMouseDown={onToolbarSelectMouseDown}
          onChange={(e) => applyBlock(e.target.value)}
          className="h-8 text-xs border border-gray-200 rounded bg-white px-2 text-gray-700 focus:outline-none"
          aria-label="Text style"
        >
          <option value="p">Text</option>
          <option value="h1">H1</option>
          <option value="h2">H2</option>
          <option value="h3">H3</option>
        </select>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => exec('bold')} className="h-8 px-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Bold">
          B
        </button>
        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => exec('italic')} className="h-8 px-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Italic">
          I
        </button>
        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => exec('underline')} className="h-8 px-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Underline">
          U
        </button>
        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => exec('strikeThrough')} className="h-8 px-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Strikethrough">
          S
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => exec('insertUnorderedList')} className="h-8 px-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Bullet list">
          •
        </button>
        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => exec('insertOrderedList')} className="h-8 px-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Numbered list">
          1.
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => wrapSelectionWithBlock('blockquote')} className="h-8 px-2 text-xs font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Quote">
          Quote
        </button>
        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={() => wrapSelectionWithBlock('pre')} className="h-8 px-2 text-sm font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Code block">
          {'</>'}
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={insertLink} className="h-8 px-2 text-xs font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Insert link">
          Link
        </button>
        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={insertImage} className="h-8 px-2 text-xs font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Insert image">
          Img
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        <button type="button" onMouseDown={onToolbarButtonMouseDown} onClick={clearFormatting} className="h-8 px-2 text-xs font-bold text-gray-700 hover:bg-gray-100 rounded" aria-label="Clear formatting">
          Clear
        </button>
      </div>
      <div className="relative">
        {isEmpty && (
          <div className="pointer-events-none absolute inset-0 px-3 py-2 text-sm text-gray-400 whitespace-pre-wrap">
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={(e) => {
            saveSelection();
            onChange(e.currentTarget.innerHTML);
          }}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onBlur={saveSelection}
          style={{
            minHeight: resolvedMinHeight,
            ...(safeMaxHeight ? { maxHeight: safeMaxHeight } : {}),
          }}
          className={`overflow-auto w-full px-3 py-2 border border-t-0 border-gray-200 rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 text-sm bg-white ${resizable ? 'resize-y' : ''} ${editorClassName} [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:leading-snug [&_h2]:text-xl [&_h2]:font-bold [&_h2]:leading-snug [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:leading-snug [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-6 [&_blockquote]:text-gray-700 [&_blockquote]:italic [&_blockquote]:text-sm [&_blockquote]:leading-relaxed [&_blockquote]:relative [&_blockquote:before]:content-['“'] [&_blockquote:before]:absolute [&_blockquote:before]:left-2 [&_blockquote:before]:top-0 [&_blockquote:before]:text-xl [&_blockquote:before]:leading-none [&_blockquote:before]:text-gray-300 [&_pre]:my-2 [&_pre]:rounded [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:font-mono [&_pre]:text-sm [&_pre]:whitespace-pre-wrap [&_code]:font-mono [&_code]:text-sm`}
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
